import React from 'react';
import { Typography, TextField, FormControlLabel, Checkbox, Grid, Button, Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

const AdvancedSettingsStep = ({ advancedSettings, setAdvancedSettings, categories, setCategories, scraperConfig, setScraperConfig }) => {
    return (
        <Grid container spacing={2}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                    Scraper Configuration
                </Typography>

                {/* All e-shops created by wizard use generic scraper */}
                <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        label="Scraper Type"
                        value="generic"
                        disabled
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                    />
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Pagination Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Next Button Selector"
                            value={advancedSettings.pagination.next_button}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                pagination: {
                                    ...advancedSettings.pagination,
                                    next_button: e.target.value
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} sx={{ mb: 1 }}>
                        <TextField
                            label="Page Parameter"
                            value={advancedSettings.pagination.page_param}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                pagination: {
                                    ...advancedSettings.pagination,
                                    page_param: e.target.value
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6} sx={{ mb: 1 }}>
                        <TextField
                            label="Max Pages"
                            type="number"
                            value={advancedSettings.pagination.max_pages}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                pagination: {
                                    ...advancedSettings.pagination,
                                    max_pages: parseInt(e.target.value) || 0
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Testing Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Product Limit"
                            type="number"
                            value={advancedSettings.testing.product_limit}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                testing: {
                                    ...advancedSettings.testing,
                                    product_limit: parseInt(e.target.value) || 0
                                }
                            })}
                            helperText="Limit number of products for testing (0 = no limit)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advancedSettings.testing.enabled}
                                    onChange={(e) => setAdvancedSettings({
                                        ...advancedSettings,
                                        testing: {
                                            ...advancedSettings.testing,
                                            enabled: e.target.checked
                                        }
                                    })}
                                    size="small"
                                />
                            }
                            label="Enable Testing Mode"
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Page Load Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Page Load Timeout (seconds)"
                            type="number"
                            value={advancedSettings.page_load_timeout || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                page_load_timeout: parseInt(e.target.value) || null
                            })}
                            helperText="Maximum time to wait for a page to load (default: 300 seconds)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    AJAX Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="AJAX Timeout (seconds)"
                            type="number"
                            value={advancedSettings.ajax_timeout || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                ajax_timeout: parseInt(e.target.value) || null
                            })}
                            helperText="Maximum time to wait for AJAX content to load (default: 10 seconds)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="AJAX Check Interval (ms)"
                            type="number"
                            value={advancedSettings.ajax_interval || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                ajax_interval: parseInt(e.target.value) || null
                            })}
                            helperText="Time interval to check if AJAX content has loaded (default: 500ms)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="AJAX Container Selectors"
                            value={advancedSettings.ajax_selectors || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                ajax_selectors: e.target.value
                            })}
                            helperText="Comma-separated CSS selectors for AJAX container elements (e.g., '.product-price-ajax,[data-elio-replace-price],[data-elio-product-id]')"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="AJAX Content Selectors"
                            value={advancedSettings.ajax_content_selectors || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                ajax_content_selectors: e.target.value
                            })}
                            helperText="Comma-separated CSS selectors that indicate actual content has loaded inside AJAX containers (e.g., '.product-price,.product-price-wrapper,.e-pm-price')"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Detail Page AJAX Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advancedSettings.ajax_wait_for_detail_page || false}
                                    onChange={(e) => setAdvancedSettings({
                                        ...advancedSettings,
                                        ajax_wait_for_detail_page: e.target.checked
                                    })}
                                    size="small"
                                />
                            }
                            label="Wait for AJAX on Detail Pages"
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            Enable this if product detail pages load content via AJAX (e.g., prices, availability)
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                    Rate Limiting
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Requests per Minute"
                            type="number"
                            value={advancedSettings.rate_limiting.requests_per_minute}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                rate_limiting: {
                                    ...advancedSettings.rate_limiting,
                                    requests_per_minute: parseInt(e.target.value) || 30
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Delay Between Requests (ms)"
                            type="number"
                            value={advancedSettings.rate_limiting.delay_between_requests}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                rate_limiting: {
                                    ...advancedSettings.rate_limiting,
                                    delay_between_requests: parseInt(e.target.value) || 1000
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Retry Settings
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Max Retries"
                            type="number"
                            value={advancedSettings.retry_settings.max_retries}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                retry_settings: {
                                    ...advancedSettings.retry_settings,
                                    max_retries: parseInt(e.target.value) || 3
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Retry Delay (ms)"
                            type="number"
                            value={advancedSettings.retry_settings.retry_delay}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                retry_settings: {
                                    ...advancedSettings.retry_settings,
                                    retry_delay: parseInt(e.target.value) || 5000
                                }
                            })}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                    Additional Features
                </Typography>
                <Grid container spacing={1}>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={advancedSettings.enable_lazy_scroll || false}
                                    onChange={(e) => setAdvancedSettings({
                                        ...advancedSettings,
                                        enable_lazy_scroll: e.target.checked
                                    })}
                                    size="small"
                                />
                            }
                            label="Enable Lazy Loading Scroll"
                        />
                        <Typography variant="caption" color="textSecondary">
                            Scroll to the end of the list to load more products
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Load More Button Selector"
                            value={advancedSettings.load_more_button_selector || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                load_more_button_selector: e.target.value
                            })}
                            helperText="CSS selector for 'Load More' button to click before scrolling (e.g., '.load-more-btn, #load-more')"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Max Load More Clicks"
                            type="number"
                            value={advancedSettings.max_load_more_clicks || 0}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                max_load_more_clicks: parseInt(e.target.value) || 0
                            })}
                            helperText="Maximum number of times to click the 'Load More' button (default: 50)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Load More Timeout (seconds)"
                            type="number"
                            value={advancedSettings.load_more_timeout || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                load_more_timeout: parseInt(e.target.value) || 12
                            })}
                            helperText="Maximum time (in seconds) to wait for new products to appear after clicking 'Load More' (default: 12)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sx={{ mb: 1 }}>
                        <TextField
                            label="Scraper Run Timeout (seconds)"
                            type="number"
                            value={advancedSettings.scraper_timeout || ''}
                            onChange={(e) => setAdvancedSettings({
                                ...advancedSettings,
                                scraper_timeout: parseInt(e.target.value) || 7200  // Default to 2 hours
                            })}
                            helperText="Maximum time allowed for a complete scraper run (default: 7200 seconds/2 hours)"
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AdvancedSettingsStep;